import React, { useState, useEffect } from "react";
import allImages from "../../../assets/images-import";
import { toast } from "react-toastify";

import {
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function MultipleFileUpload({
  onFilesChange,
  selectedFiles = [],
}) {
  const [files, setFiles] = useState(selectedFiles);

  // Sync internal state with prop changes
  useEffect(() => {
    setFiles(selectedFiles);
  }, [selectedFiles]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);

    // Additional validation to ensure only images are processed
    const imageFiles = selectedFiles.filter((file) =>
      file.type.startsWith("image/")
    );

    if (imageFiles.length !== selectedFiles.length) {
      toast.error("Only image files are allowed!");
    }

    const updatedFiles = [...files, ...imageFiles];
    setFiles(updatedFiles);

    // Notify parent component about the file changes
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }

    // Clear the input value to allow selecting the same files again if needed
    event.target.value = "";
  };

  const getImagePreview = (file) => {
    if (file.type.startsWith("image/")) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(updatedFiles);

    // Notify parent component about the file changes
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
  };

  return (
    <Box
      sx={{
        width: 400,
        p: 3,
        border: "1px dashed grey",
        borderRadius: 2,
        textAlign: "center",
      }}
    >
      <Button
        variant="contained"
        className="d-block"
        component="label"
        sx={{
          background: "none", // removes background
          border: "none", // removes border
          boxShadow: "none", // removes shadow
          padding: 0, // optional: remove default padding
          "&:hover": {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <img
          src={allImages.ImagePlaceholder}
          className={`rounded-circle avatar-xl img-thumbnail user-profile-image shadow`}
          alt="user-profile"
          style={{
            width: "120px",
            height: "120px",
            objectFit: "cover",
          }}
        />
        <p className="mt-3 mb-3 text-black">Select Files To Upload</p>
        <input type="file" hidden multiple onChange={handleFileChange} />
      </Button>

      {files.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1">Selected Files:</Typography>
          <List>
            {files.map((file, index) => (
              <ListItem
                key={index}
                divider
                sx={{
                  alignItems: "center",
                  position: "relative",
                  pr: 6, // Add right padding to make space for remove button
                }}
              >
                <Box sx={{ mr: 2 }}>
                  <Avatar
                    src={getImagePreview(file)}
                    variant="square"
                    sx={{
                      width: 50,
                      height: 50,
                      border: "1px solid #ddd",
                      borderRadius: 1,
                    }}
                  >
                    {!file.type.startsWith("image/") && "📄"}
                  </Avatar>
                </Box>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                  sx={{
                    textAlign: "left",
                    whiteSpace: "wrap",
                    wordBreak: "break-word",
                    overflow: "hidden",
                  }}
                />
                <IconButton
                  onClick={() => removeFile(index)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    width: 24,
                    height: 24,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
